import { useSettingsStore } from "@context/useSettingsStore";
import { useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { getAssignationTypesByLink } from "../graphql/assignationTypes.queries";
import { AssignationTypeFilterInput, AssignationTypePage } from "@models/ISchema";

type AssignationTypeParams = {
  originId: string | number;
  scenarioId: string | number;
  linkId: string | number;
  instructorId: string | number;
  courseType?: string;
};

export const useGetAssignationsTypesByLink = () => {
  const adaptedAssignmentsByCourseType = useSettingsStore(
    state => state.adaptedAssignmentsByCourseType,
  );
  const [requestedIds, setRequestedIds] = useState<Pick<
    AssignationTypeParams,
    "linkId" | "instructorId"
  > | null>(null);
  const courseTypeRef = useRef<string | null>(null);

  const [loadAssignationTypes, { data: rawAssignations, loading, error }] = useLazyQuery<
    {
      data: {
        assignationTypes: AssignationTypePage;
      };
    },
    {
      originId: string | number;
      scenarioId: string | number;
      filter: AssignationTypeFilterInput;
    }
  >(getAssignationTypesByLink);

  // Filter assignation types that exist in adaptedAssignments
  const filteredAssignations = useMemo(() => {
    if (!rawAssignations || !courseTypeRef.current) return [];

    /**
     * Important: Course type link should always be in lowercase to match the adaptedAssignmentsByCourseType
     */
    const courseTypeLink = courseTypeRef.current.toLowerCase();
    const courseTypeAssignments = adaptedAssignmentsByCourseType?.[courseTypeLink] || {};

    return rawAssignations?.data?.assignationTypes?.items
      ?.filter(assignation => courseTypeAssignments && !!courseTypeAssignments?.[assignation?.code])
      ?.map(assignation => ({
        label: assignation.name,
        value: assignation.id,
        data: {
          ...assignation,
          courseType: courseTypeAssignments?.[assignation.code]?.course_type_link || "",
        },
      }));
  }, [rawAssignations, adaptedAssignmentsByCourseType, courseTypeRef.current]);

  const handleLoadAssignationTypes = (params: AssignationTypeParams) => {
    if (
      !loading &&
      (params?.linkId !== requestedIds?.linkId ||
        params?.instructorId !== requestedIds?.instructorId)
    ) {
      setRequestedIds({
        linkId: params.linkId,
        instructorId: params.instructorId,
      });

      courseTypeRef.current = params.courseType || null;

      loadAssignationTypes({
        variables: {
          originId: params.originId,
          scenarioId: params.scenarioId,
          filter: {
            fields: {
              linkId: {
                eq: params.linkId as number,
              },
            },
          },
        },
      });
    }
  };

  return {
    data: filteredAssignations,
    isLoading: {
      instructorId: requestedIds?.instructorId,
      linkId: requestedIds?.linkId,
      value: loading,
    },
    error,
    loadAssignationTypes: handleLoadAssignationTypes,
  };
};
